import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FormField, Input } from "@/components/ui";
import type { PickedCity } from "./CityPicker";
import { CityPicker } from "./CityPicker";
import type { CallDraft } from "./model";

/**
 * The five things reception asks whoever is on the line.
 *
 * Name and company of everyone, and a number or an address to ring them back
 * on — either will do, but one is needed. The city when it is offered.
 * No save button: nothing here is written until the call is handed to a
 * seller, and typing is what drives the search underneath.
 */
export function CallForm({
  draft,
  onChange,
}: {
  draft: CallDraft;
  onChange: (draft: CallDraft) => void;
}) {
  const { t } = useTranslation();
  // The draft holds the id it sends; the name that goes with it lives here.
  // Compared against the draft, so a form emptied from above shows no city.
  const [city, setCity] = useState<PickedCity | null>(null);
  const picked = city && String(city.id) === draft.location ? city : null;

  function set<K extends keyof CallDraft>(key: K, value: CallDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  return (
    <div className="grid gap-5 p-4 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3">
      <FormField label={t("reception.form.contactName")}>
        {(id) => (
          <Input
            id={id}
            required
            autoFocus
            value={draft.contactName}
            maxLength={200}
            onChange={(event) => set("contactName", event.target.value)}
          />
        )}
      </FormField>

      <FormField label={t("reception.form.companyName")}>
        {(id) => (
          <Input
            id={id}
            required
            value={draft.companyName}
            maxLength={140}
            onChange={(event) => set("companyName", event.target.value)}
          />
        )}
      </FormField>

      <FormField label={t("reception.form.phone")} hint={t("reception.form.phoneHint")}>
        {(id) => (
          <Input
            id={id}
            type="tel"
            inputMode="tel"
            value={draft.phone}
            maxLength={40}
            onChange={(event) => set("phone", event.target.value)}
          />
        )}
      </FormField>

      <FormField label={t("reception.form.email")} hint={t("reception.form.emailHint")}>
        {(id) => (
          <Input
            id={id}
            type="email"
            value={draft.email}
            maxLength={80}
            onChange={(event) => set("email", event.target.value)}
          />
        )}
      </FormField>

      <CityPicker
        label={t("reception.form.city")}
        value={picked}
        onChange={(next) => {
          setCity(next);
          set("location", next ? String(next.id) : "");
        }}
      />
    </div>
  );
}
